import { useEffect, useState } from "react";
import { Link } from "@/lib/router-compat";
import { ArrowRight } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import AnimatedSection from "@/components/AnimatedSection";
import OptimizedImage from "@/components/OptimizedImage";
import { supabase } from "@/integrations/supabase/client";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  cover_image: string | null;
  published_at: string | null;
  created_at: string;
}

const Blog = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      const { data } = await supabase
        .from("blog_posts")
        .select("id, title, slug, excerpt, cover_image, published_at, created_at")
        .eq("published", true)
        .or(`published_at.is.null,published_at.lte.${new Date().toISOString()}`)
        .order("published_at", { ascending: false });
      setPosts((data as BlogPost[]) ?? []);
      setLoading(false);
    };
    fetchPosts();
  }, []);

  return (
    <Layout>
      <SEOHead
        title="Blog - JIIA Studio | Conseils Architecture d'intérieur Lyon"
        description="Retrouvez les conseils et inspirations de JIIA Studio : aménagement, décoration, tendances, plans 2D/3D et bien plus."
        canonical="/blog"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "Blog JIIA Studio",
          description: "Conseils et inspirations en architecture d'intérieur, décoration et aménagement.",
          url: "https://jiia-studio.com/blog",
          publisher: { "@type": "Organization", name: "JIIA Studio" },
        }}
      />

      <section className="py-24 md:py-32">
        <div className="container">
          <AnimatedSection>
            <p className="text-xs font-body uppercase tracking-[0.3em] text-muted-foreground mb-4">Blog</p>
            <h1 className="font-heading text-4xl md:text-5xl font-light mb-12">
              Inspirations & Conseils
            </h1>
          </AnimatedSection>

          {loading ? (
            <p className="text-muted-foreground font-body">Chargement...</p>
          ) : posts.length === 0 ? (
            <p className="text-muted-foreground font-body">Aucun article pour le moment. Revenez bientôt !</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {posts.map((post, i) => (
                <AnimatedSection key={post.id} delay={i * 0.1}>
                  <Link to={`/blog/${post.slug}`} className="group block">
                    <div className="aspect-[4/3] bg-secondary rounded-sm mb-4 overflow-hidden flex items-center justify-center">
                      {post.cover_image ? (
                        <OptimizedImage src={post.cover_image} alt={post.title} width={600} className="w-full h-full group-hover:scale-105 transition-transform duration-700" />
                      ) : (
                        <p className="text-muted-foreground font-body text-xs">Image à venir</p>
                      )}
                    </div>
                    <time className="text-xs font-body text-muted-foreground">
                      {new Date(post.published_at ?? post.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                    </time>
                    <h2 className="font-heading text-xl mt-2 mb-2 group-hover:text-accent transition-colors">
                      {post.title}
                    </h2>
                    {post.excerpt && (
                      <p className="text-sm text-muted-foreground font-body leading-relaxed mb-3">{post.excerpt}</p>
                    )}
                    <span className="inline-flex items-center gap-2 text-xs font-body uppercase tracking-widest text-foreground group-hover:text-accent transition-colors">
                      Lire l'article <ArrowRight className="h-3 w-3" />
                    </span>
                  </Link>
                </AnimatedSection>
              ))}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default Blog;
